import Styles from './styles.module.css';
import '../../salon/auth/login/tokens.css';
import React, { useContext, useEffect, useRef, useState } from 'react';
import NoIndex from '../../components/NoIndex';
import {
  IoInformationCircleOutline,
  IoLinkOutline,
  IoHomeOutline,
  IoTrendingUpOutline,
  IoSearchOutline,
  IoBookmarkOutline,
  IoBookOutline,
  IoPersonOutline,
  IoHeartOutline,
  IoSparklesOutline,
  IoCheckmarkCircleOutline,
  IoHelpCircleOutline,
  IoMenuOutline,
  IoCloseOutline,
  IoExpandOutline,
  IoBulbOutline,
  IoArrowUpOutline,
  IoCompassOutline,
  IoAlertCircleOutline,
} from 'react-icons/io5';

/*
 * 学生アプリ(memorii)の使い方マニュアル。学校管理画面マニュアル(manual/school)と
 * 同じ構成・デザイントークンを使った、学生・先生向けに公開するヘルプページ。
 * ログイン不要で誰でも開けるように認証ゲートの外にルーティングする(App.js 側)。
 *
 * 画面のスクリーンショットは public/student-manual/ に置き、/student-manual/xxx.png で参照する。
 */

type Section = {
  id: string;
  title: string;
  icon: React.ReactNode;
};

// 画像を拡大表示するためのライトボックスを開く関数。Shot は本文中の色々な場所から
// (Props を毎回渡さず) 呼べるよう、Context 経由で StudentAppManual 側の setZoom を受け取る
const ZoomContext = React.createContext<(src: string, alt: string) => void>(() => {});

// 画像のどこをタップすればいいかを示す番号ピン。x/y は画像に対する位置の割合(0-100)で指定する
// ので、260px表示でも拡大表示でも同じ場所を指せる
type Hotspot = { x: number; y: number; n: number };

// 画面のスクリーンショット1枚。キャプション付きで、枠線のついたカードとして表示する。
// 画像をクリック(タップ)すると、元の解像度に近いサイズで大きく表示する。hotspots を渡すと、
// 本文の手順(①②③)と対応する番号ピンを画像上に重ねて表示する
//
// ── caption を書くかどうかのルール(StepList との重複を防ぐため。manual/school と共通) ──
// 1. この Shot の直前(同じ SubHeading 内)に StepList があり、かつ hotspots を付けている場合、
//    caption に手順をもう一度書かない。「①…②…」のように StepList の文言をなぞる書き出しは
//    禁止。caption は省略するか、StepList に書いていない新情報だけを書く。
// 2. StepList が無い単独の Shot には、画面が何かを示す caption を必ずつける。
// 3. 判断に迷ったら「StepList を声に出して読んだときに、caption と同じ内容を2回言っていないか」
//    を基準にする。
const Shot: React.FC<{ src: string; alt: string; caption?: string; hotspots?: Hotspot[] }> = ({ src, alt, caption, hotspots }) => {
  const openZoom = useContext(ZoomContext);
  return (
    <figure className={Styles.shot}>
      <button type="button" className={Styles.shotButton} onClick={() => openZoom(src, alt)} aria-label={`${alt}を拡大表示`}>
        <img src={src} alt={alt} loading="lazy" />
        {hotspots?.map((h, i) => (
          <span key={`${i}-${h.x}-${h.y}`} className={Styles.hotspot} style={{ left: `${h.x}%`, top: `${h.y}%` }}>{h.n}</span>
        ))}
        <span className={Styles.shotZoomHint}><IoExpandOutline /></span>
      </button>
      {caption && <figcaption className={Styles.shotCaption}>{caption}</figcaption>}
    </figure>
  );
};

// 画面の説明だけでなく、実際にどう使うかを示す「活用のヒント」の箱
const TipBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={Styles.tipBox}>
    <IoBulbOutline className={Styles.tipBoxIcon} />
    <div className={Styles.tipBoxBody}>
      <p className={Styles.tipBoxTitle}>活用のヒント</p>
      <ul className={Styles.tipBoxList}>{children}</ul>
    </div>
  </div>
);

// 操作手順を①②③の番号付きで見せるための一覧
const StepList: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ol className={Styles.stepList}>{children}</ol>
);
const Step: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className={Styles.step}>{children}</li>
);

// 操作を間違えやすいポイントの注意書き(黄色)
const Caution: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={Styles.caution}>
    <IoAlertCircleOutline className={Styles.cautionIcon} />
    <div>{children}</div>
  </div>
);

// 各セクションの冒頭で「この画面で何ができるか」を1行でまとめる太字のリード文
const SectionLead: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className={Styles.sectionLead}>{children}</p>
);

// セクション内を機能のまとまりごとに分ける小見出し
const SubHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className={Styles.subHeading}>{children}</h3>
);

const SECTIONS: Section[] = [
  { id: 'start', title: 'はじめに', icon: <IoInformationCircleOutline /> },
  { id: 'account', title: 'アカウント登録と学校連携', icon: <IoLinkOutline /> },
  { id: 'home', title: 'ホーム画面の見方', icon: <IoHomeOutline /> },
  { id: 'growth', title: 'キャラクターを育てる', icon: <IoTrendingUpOutline /> },
  { id: 'discover', title: '見つける', icon: <IoSearchOutline /> },
  { id: 'saved', title: '保存する', icon: <IoBookmarkOutline /> },
  { id: 'learn', title: '学ぶ', icon: <IoBookOutline /> },
  { id: 'mypage', title: 'マイページ', icon: <IoPersonOutline /> },
  { id: 'appeal', title: 'アピール・いいね・スカウト', icon: <IoHeartOutline /> },
  { id: 'personality', title: '性格診断を受ける', icon: <IoSparklesOutline /> },
  { id: 'report', title: '内定承諾レポート', icon: <IoCheckmarkCircleOutline /> },
  { id: 'faq', title: 'よくある質問', icon: <IoHelpCircleOutline /> },
];

// 「やりたいことから探す」の導線。機能名を覚えていなくても、目的の文章から該当の章へ
// 直接ジャンプできるようにする(ページ冒頭に並べる)
const GOALS: { label: string; id: string }[] = [
  { label: 'ログインできない', id: 'account' },
  { label: 'AIに就活の相談をしたい', id: 'home' },
  { label: '気になる求人・サロンを探したい', id: 'discover' },
  { label: '見学・応募の状況を管理したい', id: 'saved' },
  { label: '国家試験の勉強をしたい', id: 'learn' },
  { label: '性格診断を受けたい', id: 'personality' },
  { label: 'サロンからのいいね・スカウトについて知りたい', id: 'appeal' },
  { label: '内定承諾レポートを提出したい', id: 'report' },
  { label: 'うまくいかない・困っている', id: 'faq' },
];

const StudentAppManual: React.FC = () => {
  const [ activeId, setActiveId ] = useState<string>(SECTIONS[0].id);
  const [ menuOpen, setMenuOpen ] = useState(false);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  // クリックで拡大表示中のスクリーンショット(無ければ非表示)
  const [ zoom, setZoom ] = useState<{ src: string; alt: string } | null>(null);
  const openZoom = (src: string, alt: string) => setZoom({ src, alt });

  // 拡大表示中は Esc キーで閉じられるようにする
  useEffect(() => {
    if (!zoom) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setZoom(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoom]);

  // スクロール位置に応じて、今読んでいる章の目次項目をハイライトする(スマホ・PC共通)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '-96px 0px -70% 0px', threshold: 0 }
    );

    SECTIONS.forEach(({ id }) => {
      const el = sectionRefs.current[id];
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const goTo = (id: string) => {
    setMenuOpen(false);
    const el = sectionRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // ページ上部へ戻るボタン。長いページなので、ある程度スクロールしたら表示する
  const [ showBackToTop, setShowBackToTop ] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <ZoomContext.Provider value={openZoom}>
      <NoIndex title="学生アプリの使い方 | memorii works" />
      <div className={Styles.page}>
        {/* スマホ用の上部バー(ロゴ代わりの見出し + 目次を開くボタン) */}
        <div className={Styles.mobileBar}>
          <span className={Styles.mobileBarTitle}>学生アプリマニュアル</span>
          <button
            type="button"
            className={Styles.menuButton}
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="目次を開く">
            {menuOpen ? <IoCloseOutline /> : <IoMenuOutline />}
          </button>
        </div>

        <div className={Styles.layout}>
          {/* 目次(PCは左に固定表示。スマホはボタンで開くシート) */}
          <nav className={`${Styles.toc} ${menuOpen ? Styles.toc_open : ''}`}>
            <p className={Styles.tocHeading}>目次</p>
            <ul className={Styles.tocList}>
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className={activeId === s.id ? `${Styles.tocItem} ${Styles.tocItem_active}` : Styles.tocItem}
                    onClick={() => goTo(s.id)}>
                    <span className={Styles.tocIcon}>{s.icon}</span>
                    {s.title}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
          {menuOpen && <div className={Styles.tocOverlay} onClick={() => setMenuOpen(false)} />}

          {/* 本文 */}
          <main className={Styles.content}>
            <header className={Styles.pageHeader}>
              <h1 className={Styles.pageTitle}>学生アプリの使い方</h1>
              <p className={Styles.pageLead}>
                memorii は、美容師を目指す専門学校生のための就活アプリです。AI就活パートナーへの相談、
                求人・サロン探し、気になるサロンの保存、国家試験対策、作品撮り、性格診断、
                内定承諾レポートの提出までを1つのアプリで行えます。アプリ上での見学予約・応募・
                ガイダンス回答などの行動は、
                <a
                  href="/manual/school"
                  target="_blank"
                  rel="noreferrer"
                  className={Styles.inlineCode}>
                  学校管理画面
                </a>
                にも共有されます。
              </p>
              <p className={Styles.pageUpdated}>最終更新日：2026年10月14日</p>
            </header>

            {/* やりたいことから探す(機能名を覚えていなくても、目的から該当の章へ飛べる) */}
            <nav className={Styles.goalBox} aria-label="やりたいことから探す">
              <p className={Styles.goalBoxTitle}>
                <IoCompassOutline className={Styles.goalBoxIcon} />
                やりたいことから探す
              </p>
              <div className={Styles.goalList}>
                {GOALS.map((g) => (
                  <button key={g.id} type="button" className={Styles.goalItem} onClick={() => goTo(g.id)}>
                    {g.label}
                  </button>
                ))}
              </div>
            </nav>

            <section
              id="start"
              ref={(el) => (sectionRefs.current['start'] = el)}
              className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoInformationCircleOutline className={Styles.sectionIcon} />
                はじめに
              </h2>
              <SectionLead>
                アプリは、画面下部の5つのタブで切り替えます。ホーム（AI相談・進み具合）、見つける
                （求人・サロン探し）、保存（気になる求人の管理）、学ぶ（国家試験対策・作品撮り）、
                マイ（プロフィール・設定）です。
              </SectionLead>

              <SubHeading>はじめて使うときの流れ</SubHeading>
              <StepList>
                <Step>
                  アカウントを登録し、
                  <button type="button" className={Styles.inlineLink} onClick={() => goTo('account')}>
                    「学校と連携」
                  </button>
                  で学校情報を入力します。
                </Step>
                <Step>
                  <button type="button" className={Styles.inlineLink} onClick={() => goTo('home')}>
                    ホーム画面
                  </button>
                  でAI就活パートナーに相談しながら、就活の進み具合を確認します。
                </Step>
                <Step>
                  <button type="button" className={Styles.inlineLink} onClick={() => goTo('discover')}>
                    「見つける」
                  </button>
                  で気になる求人・サロンを探し、
                  <button type="button" className={Styles.inlineLink} onClick={() => goTo('saved')}>
                    「保存」
                  </button>
                  で管理します。
                </Step>
                <Step>
                  <button type="button" className={Styles.inlineLink} onClick={() => goTo('learn')}>
                    「学ぶ」
                  </button>
                  で国家試験対策と作品撮りを進めます。
                </Step>
                <Step>
                  内定が決まったら
                  <button type="button" className={Styles.inlineLink} onClick={() => goTo('report')}>
                    「内定承諾レポート」
                  </button>
                  を提出して完了です。
                </Step>
              </StepList>
              <p>
                上の「やりたいことから探す」から、目的に近い項目を選んでいただくと、該当する章まで直接移動できます。
              </p>
            </section>

            <section
              id="account"
              ref={(el) => (sectionRefs.current['account'] = el)}
              className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoLinkOutline className={Styles.sectionIcon} />
                アカウント登録と学校連携
              </h2>
              <SectionLead>
                アプリを使うには、自分のアカウントを登録したうえで「学校と連携」の設定が必要です。
              </SectionLead>
              <p>
                学校と連携すると、見学予約・応募・ガイダンス回答・公欠申請などの活動が、自分の学校の
                管理画面に共有されるようになります。
              </p>

              <SubHeading>学校と連携する</SubHeading>
              <StepList>
                <Step>「マイ」タブ右上の歯車アイコンから「設定」を開きます。</Step>
                <Step>「アカウント情報」の「学校と連携」をタップします。</Step>
                <Step>氏名・学校名・コース・学年・クラス・出席番号を入力します。</Step>
              </StepList>

              <div className={Styles.shotRow}>
                <Shot
                  src="/student-manual/mypage-top.png"
                  alt="マイページ"
                  hotspots={[{ n: 1, x: 92, y: 10 }]} />
                <Shot
                  src="/student-manual/mypage-settings.png"
                  alt="設定画面"
                  hotspots={[{ n: 2, x: 50, y: 40 }]} />
                <Shot
                  src="/student-manual/mypage-school-link.png"
                  alt="学校と連携の画面"
                  hotspots={[{ n: 3, x: 50, y: 24 }]} />
              </div>

              <Caution>
                学校名・コース・学年・クラスは、一覧から必ずタップして選択してください。自分で入力しただけでは
                正しく保存されません。候補に自分の学校が見当たらない場合は、学校の就職支援担当の先生に確認しましょう。
              </Caution>

              <TipBox>
                <li>学校連携をしておくと、先生が皆さんの就活の進み具合を把握することができます。入学後はまず最初に設定しておきましょう。</li>
                <li>学校が公欠申請の機能を利用している場合は、「設定」→「就活ツール」→「公欠申請」からサロン見学や面接の公欠を申請できます。申請は学校管理画面に届き、先生が許可・不許可を判断します。</li>
              </TipBox>
            </section>

            <section
              id="home"
              ref={(el) => (sectionRefs.current['home'] = el)}
              className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoHomeOutline className={Styles.sectionIcon} />
                ホーム画面の見方
              </h2>
              <SectionLead>
                ホームは、AI就活パートナーへの相談と、就活の進み具合をまとめて確認できる画面です。
              </SectionLead>

              <SubHeading>AI就活パートナーに相談する</SubHeading>
              <StepList>
                <Step>「自分に合うサロンを探す」「求人を比較したい」などのチップをタップするか、「AIと相談をはじめる」を押します。</Step>
                <Step>AIが会話しながら、希望に合うサロンや求人を提案します。</Step>
              </StepList>
              <div className={Styles.shotRow}>
                <Shot
                  src="/student-manual/home-top.png"
                  alt="ホーム画面"
                  caption="ホーム画面。AI就活パートナーと就活の進み具合、今日やることが並ぶ"
                  hotspots={[
                    { n: 1, x: 27, y: 42 },
                    { n: 1, x: 50, y: 55 },
                  ]} />
                <Shot
                  src="/student-manual/home-ai-chat.png"
                  alt="AI就活パートナーとの会話画面"
                  caption="チップをタップすると、条件に沿ってAIが相談に応じる" />
                <Shot
                  src="/student-manual/home-ai-chat-propose.png"
                  alt="AI就活パートナーが求人を提案する画面"
                  caption="「〇〇県の給与の高いサロンを教えて」のように、自由に質問できる" />
              </div>

              <SubHeading>就活の進み具合を確認する</SubHeading>
              <p>
                保存・見学・応募・国試の件数と、育成キャラクターのゲージが表示されます。「今日やること」には、
                今日の3問などそのとき取り組むべきことがまとまっています。
              </p>
              <div className={Styles.shotRow}>
                <Shot
                  src="/student-manual/home-progress.png"
                  alt="就活の進み具合の画面"
                  caption="就活の進み具合。保存・見学・応募・国試の件数と次にやることが一目でわかる" />
                <Shot
                  src="/student-manual/home-exam-daily.png"
                  alt="今日の3問の画面"
                  caption="「今日の3問」。3分で終わる国家試験のミニ対策" />
              </div>

              <TipBox>
                <li>何から始めればいいか迷ったら、AI就活パートナーに「今の自分に合う進め方を教えて」と聞いてみましょう。</li>
                <li>「今日やること」は毎日更新されるので、ログインしたらまずここを確認する習慣をつけると就活が進みやすくなります。</li>
              </TipBox>
            </section>

            <section
              id="growth"
              ref={(el) => (sectionRefs.current['growth'] = el)}
              className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoTrendingUpOutline className={Styles.sectionIcon} />
                キャラクターを育てる
              </h2>
              <SectionLead>
                ホームの「就活の進み具合」では、就活の行動に合わせて育成キャラクターが「たまご」から
                「ニワトリ」まで成長します。
              </SectionLead>

              <StepList>
                <Step>ホーム画面の「就活の進み具合」にある「すべて見る」をタップします。</Step>
                <Step>成長の7段階と、次の姿まであとどれくらいかが表示されます。</Step>
              </StepList>

              <div className={Styles.shotRow}>
                <Shot
                  src="/student-manual/home-progress.png"
                  alt="就活の進み具合の画面"
                  caption="「すべて見る」から成長の全段階を確認できる"
                  hotspots={[{ n: 1, x: 87, y: 16.5 }]} />
                <Shot
                  src="/student-manual/growth-stages.png"
                  alt="成長段階の一覧"
                  caption="成長の7段階。見学・応募・内定・国試学習・内定承諾レポートの提出で進む"
                  hotspots={[{ n: 2, x: 50, y: 27 }]} />
                <Shot
                  src="/student-manual/growth-evolve.png"
                  alt="進化ポップアップ"
                  caption="条件を満たすと「進化した！」のポップアップで次の姿に育つ" />
              </div>

              <SubHeading>成長の段階</SubHeading>
              <ul className={Styles.list}>
                <li><strong>たまご</strong> — 気になる求人を探している段階。</li>
                <li><strong>ヒビが入った卵</strong> — 気になる求人を保存し始めた（1〜2件）。</li>
                <li><strong>ヒビが広がった卵</strong> — 求人を3件以上保存 → サロン見学に動き出す。</li>
                <li><strong>ひよこ</strong> — サロン見学を3件（予約・登録）して孵化。自分に合う働き方を探す段階。</li>
                <li><strong>成長中のひよこ</strong> — 応募・選考へ進んで、ひよこがぐんと育つ。</li>
                <li><strong>大きく育ったひよこ</strong> — 内定、または国家試験の学習が進むとさらに成長。</li>
                <li><strong>ニワトリ</strong> — 内定承諾レポートを提出すると、美容師デビューの準備が完了。</li>
              </ul>

              <TipBox>
                <li>キャラクターは、求人・作品へのいいねやプロフィールの記入なども含め、実際の就活の行動に応じて育ちます。ゲージが満タンに近いほど、次の姿まであと少しです。</li>
                <li>何をすれば育つか迷ったら、ホームの「次これしましょう！」に表示される一歩から取り組むと確実です。</li>
              </TipBox>
            </section>

            <section
              id="discover"
              ref={(el) => (sectionRefs.current['discover'] = el)}
              className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoSearchOutline className={Styles.sectionIcon} />
                見つける
              </h2>
              <SectionLead>
                「見つける」タブには、「求人探し」「スタイル写真」「内定者情報」の3つのサブタブがあります。
              </SectionLead>

              <SubHeading>求人を探す</SubHeading>
              <StepList>
                <Step>都道府県を選ぶか、右上の「フィルター」で条件を指定します。</Step>
                <Step>フリーワード検索、8タイプ分類（攻めるサロン・トレンドサロンなど）、給与などで絞り込みます。</Step>
                <Step>気になる求人カードはハート（いいね）をタップして保存します。</Step>
              </StepList>
              <div className={Styles.shotRow}>
                <Shot
                  src="/student-manual/discover-top.png"
                  alt="求人探しのトップ画面"
                  hotspots={[
                    { n: 1, x: 50, y: 29 },
                    { n: 1, x: 82, y: 17 },
                  ]} />
                <Shot
                  src="/student-manual/discover-filter.png"
                  alt="求人の絞り込み画面"
                  caption="性格診断を受けていると「マッチ率を表示」も選べるようになる"
                  hotspots={[{ n: 2, x: 50, y: 31 }]} />
                <Shot
                  src="/student-manual/discover-job-card.png"
                  alt="求人カード"
                  hotspots={[{ n: 3, x: 87, y: 25 }]} />
              </div>

              <SubHeading>スタイル写真を見る</SubHeading>
              <p>
                「スタイル写真」タブでは、気になるヘアスタイルの写真をテイストから探して見られます。気に入った
                写真はハート（いいね）をタップして保存でき、保存した写真は
                <button type="button" className={Styles.inlineLink} onClick={() => goTo('saved')}>
                  「保存」
                </button>
                タブの「スタイル」サブタブからあとで見返せます。
              </p>
              <Shot
                src="/student-manual/discover-style.png"
                alt="スタイル写真タブ"
                caption="気になるスタイルが並ぶ一覧。右上のハートをタップして保存する"
                hotspots={[{ n: 1, x: 90, y: 51 }]} />

              <SubHeading>内定者情報を見る</SubHeading>
              <p>
                「内定者情報」タブでは、色々な学校の先輩が内定した美容室と、先輩自身のインタビューを見られます。
                進路のイメージをつかむ参考にしましょう。
              </p>
              <Shot
                src="/student-manual/discover-offer-students.png"
                alt="内定者情報タブ"
                caption="内定者情報タブ。学校・サロンごとの内定者インタビューが並ぶ" />

              <TipBox>
                <li>性格診断を先に受けておくと、求人に「マッチ率」が表示されて選びやすくなります。</li>
                <li>8タイプ分類は、サロンの雰囲気を知る手がかりになります。気になるタイプを2〜3個に絞って探すと比較しやすくなります。</li>
              </TipBox>
            </section>

            <section
              id="saved"
              ref={(el) => (sectionRefs.current['saved'] = el)}
              className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoBookmarkOutline className={Styles.sectionIcon} />
                保存する
              </h2>
              <SectionLead>
                「保存」タブでは、気になる求人・スタイル写真・先輩・作品を、ステータスごとに管理できます。
                サブタブは「求人」「スタイル」「先輩」「作品」の4つです。
              </SectionLead>

              <SubHeading>気になるサロンを管理する</SubHeading>
              <p>
                「求人」サブタブ上部の「すべて／いいね／見学／応募準備中／応募済み」で、進み具合ごとに絞り込めます。
                見学の予定が決まると「見学」タブに移り、日程調整の状況が表示されます。
              </p>
              <div className={Styles.shotRow}>
                <Shot
                  src="/student-manual/saved-all.png"
                  alt="保存の全件タブ"
                  caption="保存タブ「すべて」。件数つきのステータスで一覧を絞り込める。ピンクの「保存したいサロンを登録」から下の①を開始できる"
                  hotspots={[{ n: 1, x: 25, y: 22 }]} />
                <Shot
                  src="/student-manual/saved-visit.png"
                  alt="保存の見学タブ"
                  caption="「見学」タブ。日程調整中などの状況がバッジで表示される" />
              </div>

              <SubHeading>サロンを検索して追加する</SubHeading>
              <StepList>
                <Step>「保存したいサロンを登録」をタップします。</Step>
                <Step>サロン名を検索し、該当するサロンを選びます。</Step>
                <Step>「「気になる」に登録する」を押すと保存されます。</Step>
              </StepList>
              <div className={Styles.shotRow}>
                <Shot
                  src="/student-manual/saved-add-search.png"
                  alt="サロン検索画面"
                  hotspots={[{ n: 2, x: 50, y: 40 }]} />
                <Shot
                  src="/student-manual/saved-add-confirm.png"
                  alt="サロン登録確認画面"
                  hotspots={[{ n: 3, x: 50, y: 44 }]} />
              </div>

              <SubHeading>スタイル・先輩を保存する</SubHeading>
              <p>
                「スタイル」サブタブでは気になるヘアスタイルの写真を、「先輩」サブタブでは気になる先輩の
                インタビューを、同じようにハートで保存できます。
              </p>
              <div className={Styles.shotRow}>
                <Shot
                  src="/student-manual/saved-style.png"
                  alt="保存のスタイルタブ"
                  caption="保存した「スタイル」の一覧" />
                <Shot
                  src="/student-manual/saved-senior.png"
                  alt="保存の先輩タブ"
                  caption="保存した「先輩」の一覧。学校名・サロン名つきで表示される" />
              </div>

              <TipBox>
                <li>迷っているサロンはまず「いいね」で保存し、比較してから本命を「見学」に進めると管理しやすくなります。</li>
                <li>見学の日程は、メッセージでサロンとやり取りして確定します。</li>
              </TipBox>
            </section>

            <section
              id="learn"
              ref={(el) => (sectionRefs.current['learn'] = el)}
              className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoBookOutline className={Styles.sectionIcon} />
                学ぶ
              </h2>
              <SectionLead>
                「学ぶ」タブには「国家試験」「作品撮り」の2つのサブタブがあります。
              </SectionLead>

              <SubHeading>国家試験対策をする</SubHeading>
              <p>
                「今日の3問」から毎日の学習を始められます。正答率・解答数・連続学習日数に加えて、
                関係法規・制度／衛生管理／保健／香粧品化学／文化論・美容技術理論の分野別に学習状況を確認でき、
                解答済み・間違えた問題もあとから見返せます。
              </p>
              <Shot
                src="/student-manual/learn-exam.png"
                alt="国家試験タブ"
                caption="国家試験タブ。今日の3問が完了すると「今日は完了」に変わり、分野別の学習状況が進捗バーで確認できる" />

              <p>
                「解答済み問題」「間違えた問題」からは、○×形式で問題を解き直せます。回答すると、その場で
                正解・不正解と「あなたの回答」が色分けして表示され、「出題元を見る」から出典も確認できます。
              </p>
              <div className={Styles.shotRow}>
                <Shot
                  src="/student-manual/learn-exam-wrong.png"
                  alt="国家試験の解き直し画面(不正解の例)"
                  caption="不正解のときはピンクの枠で表示される" />
                <Shot
                  src="/student-manual/learn-exam-correct.png"
                  alt="国家試験の解き直し画面(正解の例)"
                  caption="正解のときは緑の枠で表示され、「出題元を見る」から出典を確認できる" />
              </div>

              <SubHeading>作品を撮る・投稿する</SubHeading>
              <p>
                「作品撮り」サブタブでは、タグから表現したいテイストを選んで作品を投稿できます。作品をタップすると、
                いいねや関連作品も見られる詳細ページが開きます。
              </p>
              <div className={Styles.shotRow}>
                <Shot
                  src="/student-manual/learn-works.png"
                  alt="作品撮りタブ"
                  caption="作品の一覧。ハートでいいねしたり、自分の作品を投稿したりできる" />
                <Shot
                  src="/student-manual/learn-works-list.png"
                  alt="作品一覧画面"
                  caption="「今日のインスピレーション」と、テイストから探す作品一覧" />
                <Shot
                  src="/student-manual/learn-works-detail.png"
                  alt="作品の詳細ページ"
                  caption="作品の詳細ページ。投稿者と関連作品を確認できる" />
              </div>

              <TipBox>
                <li>「今日の3問」は3分で終わるので、通学中などのすき間時間に取り組むと継続しやすいです。</li>
                <li>投稿した作品はサロンへのアピールの「Pick Up Works」にも使えるので、気に入っている作品は早めに投稿しておきましょう。</li>
              </TipBox>
            </section>

            <section
              id="mypage"
              ref={(el) => (sectionRefs.current['mypage'] = el)}
              className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoPersonOutline className={Styles.sectionIcon} />
                マイページ
              </h2>
              <SectionLead>
                「マイ」タブでは、自分のプロフィール（My Card）、就活準備（履歴書・性格診断）、投稿した作品、
                各種設定をまとめて確認できます。①右上の歯車アイコンから「設定」を開くと、「アカウント情報」
                「就活ツール」「サポート」「アカウント管理」の4つにまとまっています。
              </SectionLead>
              <div className={Styles.shotRow}>
                <Shot
                  src="/student-manual/mypage-top.png"
                  alt="マイページ"
                  caption="マイページ。My Card、成長ゲージ、就活準備、My Worksが並ぶ"
                  hotspots={[{ n: 1, x: 92, y: 10 }]} />
                <Shot
                  src="/student-manual/mypage-settings.png"
                  alt="設定画面"
                  caption="①の歯車アイコンから開く設定画面。My Card編集・学校と連携・履歴書・性格診断・レポートなどをまとめて開ける" />
              </div>

              <SubHeading>My Cardを編集する</SubHeading>
              <p>
                My Cardは、見学予定のサロンに表示される自己紹介カードです。ユーザーネーム・一言メッセージ・
                卒業年月・SNSアカウントを登録できます。
              </p>
              <div className={Styles.shotRow}>
                <Shot
                  src="/student-manual/mypage-card-edit.png"
                  alt="My Card編集画面"
                  caption="My Card編集画面。ユーザーネーム・一言メッセージ・卒業年月・SNSを登録" />
                <Shot
                  src="/student-manual/mypage-card-preview.png"
                  alt="My Cardのプレビュー"
                  caption="サロン側から見えるMy Cardのプレビュー。Pick Up WorksとSNSが表示される" />
              </div>

              <TipBox>
                <li>My Cardの内容は見学先のサロンにそのまま表示されます。見学の前に一言メッセージと作品を整えておくと、印象がよくなります。</li>
                <li>履歴書は「設定」→「就活ツール」→「履歴書」からいつでも下書きを保存できます。応募の直前にまとめて書こうとせず、早めに作っておくのがおすすめです。</li>
              </TipBox>
            </section>

            <section
              id="appeal"
              ref={(el) => (sectionRefs.current['appeal'] = el)}
              className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoHeartOutline className={Styles.sectionIcon} />
                サロンへのアピール・いいね・スカウト
              </h2>

              <SubHeading>サロンへのアピールを編集する</SubHeading>
              <SectionLead>
                見学先のサロンにだけ表示される自己アピールです。My Style（なりたいスタイルのタグ、最大3件）、
                Pick Up Works（投稿済み作品から最大3件）、Growth Log（成長記録）を登録できます。
              </SectionLead>
              <Shot
                src="/student-manual/appeal-edit.png"
                alt="サロンへのアピール編集画面"
                caption="サロンへのアピール編集。My Style・Pick Up Works・Growth Logを設定する" />

              <SubHeading>サロンからの「いいね」の仕組み</SubHeading>
              <p>
                サロン側の画面には、学生の作品がみんな一覧で並んでいます。その中で感性の合う作品に、サロンから
                「いいね」が届くことがあります。いいねが来ると、作品にそのサロンのロゴが表示されるようになります。
              </p>
              <Shot
                src="/student-manual/appeal-like.png"
                alt="サロンからのいいねの説明画面"
                caption="サロンからいいねが届くと、作品にロゴが表示される（イメージ）" />

              <SubHeading>スカウトを受け取る</SubHeading>
              <StepList>
                <Step>「サロンからのスカウトを受け取る」のスイッチをオンにします。</Step>
                <Step>名前・学校名・学年・アカウントを入力します。</Step>
              </StepList>
              <p>
                オンにすると、自分の作品や希望条件にマッチしたサロンから通知が届きます。通知が来るだけで、
                いきなり直接連絡が来ることはないので、気軽にオンにして大丈夫です。
              </p>
              <Shot
                src="/student-manual/appeal-scout.png"
                alt="スカウト設定画面"
                hotspots={[
                  { n: 1, x: 85, y: 18 },
                  { n: 2, x: 50, y: 54 },
                ]} />

              <TipBox>
                <li>Pick Up Worksは見学先ごとに表示が変わるわけではなく、設定した作品がそのまま見学先のサロンに見えます。見学の前に見直しておきましょう。</li>
                <li>スカウトは通知だけなので、まずはオンにして様子を見てから、気になるサロンがあれば自分から「見学」に進めるのがおすすめです。</li>
              </TipBox>
            </section>

            <section
              id="personality"
              ref={(el) => (sectionRefs.current['personality'] = el)}
              className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoSparklesOutline className={Styles.sectionIcon} />
                性格診断を受ける
              </h2>
              <SectionLead>
                将来の美容師像を診断し、自分の価値観と美容室のマッチ度を見られる機能です。「設定」→
                「就活ツール」→「性格診断」から受けられます。
              </SectionLead>

              <StepList>
                <Step>「診断を開始する」を押します。</Step>
                <Step>各質問に「そう思う」〜「そう思わない」の7段階で答えます。</Step>
                <Step>回答が終わると、自分のタイプと4つの特性が表示されます。</Step>
              </StepList>

              <div className={Styles.shotRow}>
                <Shot
                  src="/student-manual/personality-top.png"
                  alt="性格診断トップ画面"
                  caption="診断前は「未診断」と表示される"
                  hotspots={[{ n: 1, x: 50, y: 37 }]} />
                <Shot
                  src="/student-manual/personality-question.png"
                  alt="性格診断の質問画面"
                  hotspots={[{ n: 2, x: 50, y: 47 }]} />
                <Shot
                  src="/student-manual/personality-result.png"
                  alt="性格診断の結果画面"
                  caption="特性の例: 関係性・顧客志向など" />
              </div>

              <TipBox>
                <li>
                  診断結果は求人の「マッチ率」に使われます。
                  <button type="button" className={Styles.inlineLink} onClick={() => goTo('discover')}>
                    「見つける」
                  </button>
                  で求人を探す前に受けておくと、自分に合うサロンを見つけやすくなります。
                </li>
                <li>結果はいつでも受け直せます。学年が上がって考え方が変わったタイミングで見直してみましょう。</li>
              </TipBox>
            </section>

            <section
              id="report"
              ref={(el) => (sectionRefs.current['report'] = el)}
              className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoCheckmarkCircleOutline className={Styles.sectionIcon} />
                内定承諾レポートを提出する
              </h2>
              <SectionLead>
                サロンから内定をもらい、承諾する意思が固まったら、「内定承諾レポート」を提出します。
                「設定」→「就活ツール」→「レポート」から入力できます。
              </SectionLead>

              <StepList>
                <Step>インタビュー写真（自分の写真）を設定します。</Step>
                <Step>名前・内定サロン名・出身学校などの基本情報を入力します。</Step>
                <Step>「美容学生時代に頑張った事は？」などのインタビュー質問に答えます。</Step>
                <Step>入力が終わったら、画面を一番下までスクロールし、提出ボタンを押します。</Step>
              </StepList>

              <Shot
                src="/student-manual/report-accept.png"
                alt="内定承諾レポート画面"
                caption="1枚の画面を下にスクロールしながら、①②③の順に入力していく(この先に④提出ボタンがある)"
                hotspots={[
                  { n: 1, x: 50, y: 31 },
                  { n: 2, x: 50, y: 55 },
                  { n: 3, x: 50, y: 94 },
                ]} />

              <p>
                提出すると、
                <button type="button" className={Styles.inlineLink} onClick={() => goTo('growth')}>
                  育成キャラクター
                </button>
                が最終形の「ニワトリ」まで成長し、美容師デビューの準備が完了したことになります。
                後輩の「内定者情報」にも載ることがあります。
              </p>

              <TipBox>
                <li>インタビューの回答は、自分の就活を振り返る良い機会です。焦らず、頑張ったことや決め手になった理由を具体的に書いてみましょう。</li>
              </TipBox>
            </section>

            <section
              id="faq"
              ref={(el) => (sectionRefs.current['faq'] = el)}
              className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoHelpCircleOutline className={Styles.sectionIcon} />
                よくある質問
              </h2>
              <SectionLead>
                自分で解決できそうにない場合は、「設定」→「サポート」の企業情報から、運営（memorii）に
                お問い合わせください。
              </SectionLead>
              <div className={Styles.faqItem}>
                <p className={Styles.faqQ}>Q. ログインできません。</p>
                <p className={Styles.faqA}>
                  A. 登録したメールアドレス・パスワードをご確認ください。学校で配布されたアカウントではなく、
                  自分で登録したアカウントでログインします。改善しない場合は運営までお問い合わせください。
                </p>
              </div>
              <div className={Styles.faqItem}>
                <p className={Styles.faqQ}>Q. 「学校と連携」で学校名やクラスが選べません。</p>
                <p className={Styles.faqA}>
                  A. 学校名・コース・学年・クラスは一覧から選択する項目です。候補に自分の学校やクラスが
                  見当たらない場合は、学校の就職支援担当の先生に確認してください。
                </p>
              </div>
              <div className={Styles.faqItem}>
                <p className={Styles.faqQ}>Q. 保存したはずのサロンが見当たりません。</p>
                <p className={Styles.faqA}>
                  A. ステータスが「見学」や「応募」に変わると、表示されるタブが切り替わります。「保存」タブ
                  上部の「すべて」を選ぶと、ステータスに関わらず全件を確認できます。
                </p>
              </div>
              <div className={Styles.faqItem}>
                <p className={Styles.faqQ}>Q. たまごがなかなか育ちません。</p>
                <p className={Styles.faqA}>
                  A. キャラクターの成長は、気になる求人の保存・サロン見学・応募・内定承諾レポートの提出など、
                  実際の就活の行動に応じて進みます。ホームの「次これしましょう！」に表示される次の一歩から
                  取り組んでみてください。
                </p>
              </div>
              <div className={Styles.faqItem}>
                <p className={Styles.faqQ}>Q. 性格診断はやり直せますか。</p>
                <p className={Styles.faqA}>
                  A. 「設定」→「就活ツール」→「性格診断」から何度でも受け直せます。求人の「マッチ率」には
                  最新の結果が使われます。
                </p>
              </div>
              <div className={Styles.faqItem}>
                <p className={Styles.faqQ}>Q. 内定承諾レポートはいつ提出すればいいですか。</p>
                <p className={Styles.faqA}>
                  A. サロンから内定をもらい、承諾する意思が固まったタイミングで提出します。提出すると、
                  キャラクターが最終形の「ニワトリ」まで育ちます。
                </p>
              </div>
            </section>
          </main>
        </div>
      </div>

      {/* ページ上部へ戻る(ある程度スクロールしたときだけ表示) */}
      {showBackToTop && (
        <button
          type="button"
          className={Styles.backToTop}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="ページ上部へ戻る">
          <IoArrowUpOutline />
        </button>
      )}

      {/* 画像の拡大表示(クリックで開く。背景クリック・✕・Escで閉じる) */}
      {zoom && (
        <div className={Styles.lightbox} role="dialog" aria-modal="true" aria-label={zoom.alt}>
          <div className={Styles.lightboxBg} onClick={() => setZoom(null)} />
          <button type="button" className={Styles.lightboxClose} onClick={() => setZoom(null)} aria-label="閉じる">
            <IoCloseOutline />
          </button>
          <img className={Styles.lightboxImg} src={zoom.src} alt={zoom.alt} />
        </div>
      )}
    </ZoomContext.Provider>
  );
};

export default StudentAppManual;
